import { isBrowser } from './DiligentEnv.js';
import { setEnvInfo, authToken } from './settings.js';
import TaskRequest from './TaskRequest.js';
import { getServerUrl } from '../constants/Meta';
import crypto from 'crypto-js';
import { elasticLogError, elasticLogEvent, elasticLogTaskAction, getMachineUid } from './ElasticLog';
import sleep from 'sleep-promise';
import { reloadNoCache, fetchRetry } from './Network';
import { STEP_STATUS, STEP_TYPE, TASK_STATUS, TASK_TAG, TASK_TYPE } from '../constants/Tasks.js';
import { ONE_DAY_TICKS } from '../constants/DateTime';
import { batchLookup } from './TrackedTasks.js';
import { StepStatus } from './StepStatus.js';
import { getKioskSettings } from '../utils/kioskSettingsUtils';
import { getConfig } from '../utils/configUtils';
import { getShared } from './Utils';
import { REQUIRED_ROLE } from '../constants/Tasks.js';

const deliveryType = 'point_to_point_delivery_w_robot_step_type_map_w_secure_goto';

let unsubscribers = [];
let _robotIdToName, _robotNameToId;
let lockType = undefined;
let lockNames = undefined;
let unitName = undefined;
let unitWp = undefined;
let robotId = undefined;
let isConfigured = false;
let _taskData = [];
let _robotData = [];
let _taskQueues = {};
let _orderedSteps = [];

let nextArgSubId = 1;
let argSubscribers = [];
let argTypes = undefined;
export const argSubscribe = (cb) => {
  let id = nextArgSubId++;
  argSubscribers.push({ id, cb });
  if (argTypes) setTimeout(() => cb(argTypes));
  return () => (argSubscribers = argSubscribers.filter((sub) => sub.id != id));
};

export const argPublish = (change) => {
  argTypes = change;

  let unitCode = getUnitWp(true);
  if (unitCode) {
    let unitNameMatch = Object.keys(argTypes.ids.mox_spots).find((hname) => argTypes.ids.mox_spots[hname] == unitCode);
    if (unitNameMatch && unitName != unitNameMatch) {
      unitName = unitNameMatch;
    }
  }

  argSubscribers.forEach((sub) => sub.cb(change));
};

function makeTwoWayMap(friendlyToCode) {
  let codeToFriendly = {};
  Object.keys(friendlyToCode).forEach((friendly) => {
    let code = friendlyToCode[friendly];
    if (code)
      //this 'if' necessary to deal with vestigial None:null entry in robots
      codeToFriendly[code] = friendly;
    else delete friendlyToCode[friendly]; //part 2 of dealing with None:null
  });
  return { friendlyToCode, codeToFriendly };
}

let codeMaps;

argSubscribe(async (argTypes) => {
  codeMaps = {
    idsMoxspots: makeTwoWayMap(argTypes.ids.mox_spots),
    // user_types: makeTwoWayMap(argTypes.ids.user_types),
    waypointsMoxspots: makeTwoWayMap(argTypes.waypoints.mox_spots),
    robots: makeTwoWayMap(argTypes.ids.robots),
  };
});

export function getsIdsMoxspotNames() {
  let names = Object.keys(codeMaps.idsMoxspots.friendlyToCode);
  names.sort();
  return names;
}

export function getRobotNames() {
  return Object.keys(codeMaps.robots.friendlyToCode);
}

let serverUrl;

let reloadTaps = [];

const idChars = 'abcdefghijklmnopqrstuvwxyz0123456789';

const RELOG_KEY = 'relog_key'; //persisted
let relogData = undefined;

const CONFIG_KEY = 'config';

export const START_GOAL = 'start_goal';
export const END_GOAL = 'end_goal';


export function stashConfig(config) {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
}

export function getStashedConfig() {
  let unparsed = localStorage.getItem(CONFIG_KEY);
  if (!unparsed) return undefined;

  try {
    let parsed = JSON.parse(unparsed);
    return parsed;
  }
  catch (err) {
    return undefined;
  }
}

function initRelog() {
  if (!relogData) {
    let storageAccess = false;
    let v = '' + Date.now();
    try {
      const TEST = 'test_key';
      localStorage.setItem(TEST, v);
      storageAccess = localStorage.getItem(TEST) == v;
      if (!storageAccess) console.log('initRelog: no local storage?');
      else localStorage.removeItem(TEST);
    }
    catch (err) {
      console.log('initRelog: ' + err);
    }
    relogData = { storageAccess };
    if (!storageAccess) elasticLogEvent({ what: 'noStore', msg: 'No access to localStorage' });
  }

  return relogData.storageAccess;
}

export function canRelog() {
  return initRelog() && relogData.currentConfig;
}

export function registerRelog(data) {
  if (initRelog()) {
    let stringified = JSON.stringify(data);
    relogData.currentConfig = crypto.AES.encrypt(stringified, idChars).toString();
  }
}

let clearStashTimeout;
export function setRelog(opts, expMinutes = 2) {
  if (clearStashTimeout) {
    clearTimeout(clearStashTimeout);
    clearStashTimeout = undefined;
  }
  if (initRelog() && relogData.currentConfig) {
    let when = Date.now();
    let expDeltaTicks = Math.floor(1000 * 60 * expMinutes);
    let exp = when + expDeltaTicks;
    let data = { v: 1, when, exp, data: relogData.currentConfig, ...opts };
    localStorage.setItem(RELOG_KEY, JSON.stringify(data));

    clearStashTimeout = setTimeout(() => clearRelog(), expDeltaTicks); //not a forever thing... use or lose
  }
}

export function getRelog() {
  if (!initRelog()) return undefined;

  let parseTarget = '(empty)';
  try {
    let value = localStorage.getItem(RELOG_KEY);
    if (value) {
      const now = Date.now();
      if (value.exp) {
        if (now > value.exp) {
          localStorage.removeItem(RELOG_KEY);
          return undefined;
        }
      }
      else if (now - value.when > 1000 * 60 * 2) {
        localStorage.removeItem(RELOG_KEY);
        return undefined;
      }

      parseTarget = value;
      let parsed = JSON.parse(value);
      let uncrypted = crypto.AES.decrypt(parsed.data, idChars).toString(crypto.enc.Utf8);
      parseTarget = uncrypted;
      let data = JSON.parse(uncrypted);

      return { data, userAck: parsed.userAck };
    }
  }
  catch (err) {
    console.log('getRelog error: ' + err);
    elasticLogError({ what: 'getRelog', msg: '' + err, parseTarget });
    return undefined;
  }
}

export function clearRelog() {
  localStorage.removeItem(RELOG_KEY);
}

export function shortId(uuid) {
  if(!uuid) return uuid;
  return uuid.slice(0, 6);
}

let _loadScreenShowFunc;

export function setLoadScreenShowFunc(loadScreenShowFunc) {
  _loadScreenShowFunc = loadScreenShowFunc;
}

async function showReloadScreen() {
  if (!_loadScreenShowFunc) return {}; //useSameConfig, cancel

  let result;
  _loadScreenShowFunc((resultParam) => (result = resultParam));
  while (!result) await sleep(250);

  return result;
}

export async function countForReload(params) {
  let label = params ? params.label : undefined;
  let side = params ? params.side : undefined;
  let sideAgnostic = true; //!!params.sideAgnostic;

  const windowSeconds = 3;

  const now = Date.now();
  reloadTaps = reloadTaps.filter(({ when }) => now - when < 1000 * windowSeconds);

  elasticLogTaskAction({
    what: 'utap',
    msg: `Tapped, ${label}, recent tap count was ` + reloadTaps.length,
    label,
    priorCount: reloadTaps.length,
  });

  reloadTaps.unshift({ when: now, side });
  while (reloadTaps.length > 3) reloadTaps.pop();
  if (reloadTaps.length == 3 && now - reloadTaps[2].when < 1000 * windowSeconds) {
    let doReload = true;

    if (sideAgnostic) {
      //do nothing; be side-agnostic for preConfig phase
    }
    else {
      //if all three entries have sides and they're NOT opposites (i.e. not LRL or RLR), scuttle
      if (!reloadTaps.find(({ side }) => !side)) {
        let sides = reloadTaps.map(({ side }) => side).join('');
        doReload = sides == 'RLR' || sides == 'LRL';
        if (!doReload) elasticLogEvent({ what: 'sideTaps', msg: `Not reloading: ${sides}` });
      }
    }

    let useSameConfig = false;
    if (doReload) {
      if (initRelog() && relogData.currentConfig) {
        let showScreenResult = await showReloadScreen();
        if (showScreenResult.cancel) return;
        useSameConfig = !!showScreenResult.useSameConfig;
      }
      else {
        elasticLogEvent({ what: 'tapLoad', type: 'none', msg: `No registered config` });
      }

      if (useSameConfig) setRelog({ userAck: true });

      setTimeout(() => reloadNoCache(), 1000); //escape time for logs
      return true;
    }
  }
}

/*
Produces an outcome value for a command script as interpreted on the local machine.

Takes a cmdArray that is an array of lines (strings), where each line
has >= 0 condition clauses followed by an outcome.
The first line to match this OD instance dictates the outcome. 
No matching line means the command does not apply.

A condition clause is a clauseType followed by either [a, b, c...] or ![a, b, c...]; respectively
a "is in" or a "is not in".  Items within the [] braces are comma-separated.
On any given line, the first non-matching condition clause abandons processing of that line and 
moves on to the the next line.

A clauseType can be any of: 

  code    - the station code (the NON-human-friendly kiosk code), or moxiN; e.g "lab" or "moxi21"

  id      - the instance id (as visible in ODView on name hover); will 
              check startsWith and endsWith for match

  type    - "robot" or "kiosk"; inclusion of any other types (e.g. typos) will  
              invalidate the clause (to prevent ! from doing unintended things)

  vNum    - values to be compared against getConfig().version.branch; eg. 11.7.25

  session - values to be compared against getShared().hallpassSession

  -----
  DEPRECATED: version is deprecated because it's unintuitive (not the version number) 
    And because it varies from site to site based on the precise time of the build, even 
    for the same codebase.

  version - values to be compared against getConfig().version.time, e.g. 250210.1208
  -----


outcomes can be any of:

  done    - signifies no action should be taken
  reload  - signifies that OD should reload
  ping    - OD should write a log record (for log-analytical purposes and/or testing connectivity remotely)
  stash   - OD should stash relog info in anticipation of (nominally) a hexnode policy tweak
  clear   - OD should clear any stashed relog info


Example scripts:

  reloads every instance unconditionally
    reload  

  reloads all instances that are not configured as robots and are not already on version 07.27_15:15
    type ![robot] version ![07.27_15:15] reload


It is currently anticipated that most update commands 
(1) will be put in place briefly and then removed from firestore once effected, and
(2) will have the simple form "version ![some_version] reload".

As long as we only have one version of ODWeb at a given site, then deliberately 
NOT loading that one version on a subset of machines would seem to be a mere delay of 
the inevitable.  Even so, there are cases where selectivity can be useful, e.g. 
- to spot-test something briefly
- to goose-update all kiosks and leave robot updates to be overseen by Ops
*/
export function interpretCommand(cmdArray) {
  if (!cmdArray) return 'done';

  let lines = cmdArray.map((l) => l.trim()).filter((x) => !!x); //lines are trimmed and non-trivial
  const badSyntax = (line) => {
    elasticLogEvent({ what: 'cmdInterp', msg: `Did not understand: ` + line });
    return 'done';
  };
  for (let line of lines) {
    let rem = line;
    while (rem) {
      //check for outcomes
      if (['done', 'ping', 'reload', 'stash', 'clear'].indexOf(rem) >= 0) return rem;

      //process a type
      let m = /^(code|id|type|version|vNum|session)/.exec(rem);
      if (!m) return badSyntax(line);
      let clauseType = m[1];
      rem = rem.slice(clauseType.length).trim();
      let isNot = rem.startsWith('!');
      if (isNot) rem = rem.slice(1).trim();
      if (!rem.startsWith('[')) return badSyntax(line);
      rem = rem.slice(1).trim();
      let idx = rem.indexOf(']');
      if (idx < 0) return badSyntax(line);
      let values = rem.slice(0, idx).trim().split(',').map((v) => v.trim()).filter((x) => !!x);
      rem = rem.slice(idx + 1).trim();

      if (!updateCmdClauseMatch(clauseType, isNot, values)) break; //go on to the next line
    }
  }

  return 'done';
}


function updateCmdClauseMatch(clauseType, isNot, values) {

  const alignNot = bool => isNot ? !bool : !!bool;

  if (clauseType == 'code')
    return alignNot(values.indexOf(getRobotId() || getUnitWp()) >= 0);

  else if (clauseType == 'vNum')
    return alignNot(values.indexOf(getConfig().version?.branch) >= 0);

  else if (clauseType == 'version')
    return alignNot(values.indexOf(getConfig().version?.time) >= 0);

  else if (clauseType == 'session')
    return alignNot(values.indexOf(getShared().hallpassSession) >= 0);

  else if (clauseType == 'id') {
    const localMachineId = getMachineUid();
    return alignNot(values.find((v) => localMachineId.startsWith(v) || localMachineId.endsWith(v)));
  }

  else if (clauseType == 'type') {
    values = values.map((v) => (({ kiosks: 'kiosk', robots: 'robot' }[v] || v).toLowerCase())); //choosing to forgive accidental plurals and miscaps here
    let unrecognized = values.find((v) => ['robot', 'kiosk'].indexOf(v) < 0);
    if (unrecognized) {
      elasticLogError({ what: 'cmdInterp', msg: `updateCmdClauseMatch: invalid type: ` + unrecognized }, `cmdRogueType`);
      return false;
    }
    return alignNot(values.indexOf(isRobot() ? 'robot' : 'kiosk') >= 0);
  }

  //else catch-all
  elasticLogEvent({ what: 'cmdInterp', msg: `updateCmdClauseMatch: unrecognized clauseType: ` + clauseType }, `cmdUnrecognizedClause`);
  return false;
}

export function randomId(length = 16) {
  let result = [];
  while (result.length < length) result.push(idChars.charAt(Math.floor(Math.random() * idChars.length)));
  return result.join('');
}

export function robotIdToName(robotId) {
  return _robotIdToName[robotId];
}

export function robotNameToId(robotName) {
  return _robotNameToId[robotName];
}

export function stopListeners() {
  while (unsubscribers.length) {
    try {
      unsubscribers.pop()();
    }
    catch (err) {
      console.log('\n\n******************************************');
      console.log('stopListeners: error unsubscribing: ' + err); //snuff
      console.log('******************************************\n\n');
    }
  }
}

function summarizeTask(task) {
  if (!task) return {};

  let { uuid, taskArgs, status, result, currentStepIdx, currentStepType, numberInQueue, claimedBy, requestedBy } = task;

  let { start_goal, end_goal } = taskArgs;
  start_goal = start_goal.value;
  end_goal = end_goal.value;

  return {
    uuid,
    start_goal,
    end_goal,
    status,
    result,
    currentStepIdx,
    currentStepType,
    numberInQueue,
    claimedBy,
    requestedBy,
  };
}

export function getLockType() {
  if (!lockType) throw new Error('getLockType called before value known.');
  return lockType;
}

/**
 * @return {array of string} An array of the names of the hardware containers on the robot.
 */
export function getLocktubeIds() {
  if (!lockNames) throw new Error('getLocktubeIds called before value known.');
  return lockNames;
}

//the human-friendly name
export function getUnitName() {
  if (!unitName) throw new Error('getUnitName called before value known.');
  return unitName;
}

/*
  This function exists to give us the ability to know which robot a given robot
  ipad is configured as, despite the fact that we are (at least for now) obscuring
  that information from the public.  This func looks at whatever number a given
  robot is (e.g. SN42's number is 42) and returns a string consisting of the number
  reversed and then trail-padded with zeros to a length of 4.

  This value is not displayed in the usual UI (initial consumer is the tapped-version info).
*/
export function getObfuscatedRobotSN() {
  if (!unitName) {
    console.log('getObfuscatedRobotSN called before value known.');
    return '';
  }
  if (!robotId) return ''; //not robot SN for non-robots

  let match = /([0-9]+)/.exec(unitName);
  if (!match) return '';
  let sn = match[1];
  sn = sn.padStart(4, '0');
  sn = [...sn];
  sn.reverse();
  sn = sn.join('');

  return sn;

  // let hashStr = Math.abs(stringHash(unitName)).toString(16);

  // let obfuscated = hashStr.slice(0, 3) + sn + hashStr.slice(3, 6);
  // return obfuscated;
}

export function stringHash(str) {
  if (!str.length) return 0;

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    let chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

export function getDeliveryType() {
  if (!deliveryType) throw new Error('getDeliveryType called before value known.');
  return deliveryType;
}

export function getUnitWp2(noThrow) {
  if (!unitWp && !noThrow) {
    throw new Error('getUnitWp called before value known.');
  }
  return unitWp;
}

//the raw moxspot code (NOT the human friendly name)
export function getUnitWp(noThrow) {
  if (!unitWp && !noThrow) {
    throw new Error('getUnitWp called before value known.');
  }
  return unitWp;
}





export function getRobotId() {
  if (!isConfigured) throw new Error('getRobotId called before value known.');
  return robotId;
}


export function safeGetRobotId() {
  if (!isConfigured) return undefined;
  return robotId;
}


export function isRobot() {
  if (!isConfigured) throw new Error('isRobot called before unit configured.');
  return !!robotId;
}

export function isKiosk() {
  if (!isConfigured) throw new Error('isKiosk called before unit configured.');
  return !robotId;
}

export async function waitForCodeMaps(maxWaitSecs = 30) {
  maxWaitSecs = Math.min(0, maxWaitSecs);
  if (!codeMaps) {
    const start = Date.now();
    while (!codeMaps) {
      elasticLogEvent({ what: 'mapWait', msg: `waitForCodeMaps: codeMaps was not initially populated` });
      await sleep(100);
      if (Date.now() - start > 1000 * maxWaitSecs)
        throw new Error(`waitForCodeMaps: timed out before codeMaps populated`);
    }
    const popGap = Date.now() - start;
    if (popGap > 100)
      elasticLogEvent({ what: 'delayedMaps', msg: `waitForCodeMaps: codeMaps took ${popGap} ticks to populate` });
  }
}

export async function configureUnit(station, robot, lockTypeParam, lockNamesParam) {
  if (!isBrowser() && (!lockTypeParam || !lockNamesParam))
    throw new Error('configureUnit: required parameters were not supplied');

  await waitForCodeMaps();

  if (robot) {
    robotId = unitWp = robot.id || codeMaps.robots.friendlyToCode[robot.friendly] || 'Error';
    unitName = robot.friendly || codeMaps.robots.codeToFriendly[robot.id] || 'Error';
  }
  else if (station) {
    unitWp = station.id || codeMaps.idsMoxspots.friendlyToCode[station.friendly] || 'Error';
    unitName = station.friendly || codeMaps.idsMoxspots.codeToFriendly[station.id] || 'Error';
    robotId = undefined;
  }
  else {
    elasticLogError({ what: 'cfgErr', msg: `configureUnit: No robot or station specified` });
  }

  isConfigured = true;

  lockType = lockTypeParam;
  if (lockNamesParam && !Array.isArray(lockNamesParam))
    throw new Error('configureUnit: lockNamesParam parameter must be an array');
  lockNames = lockNamesParam;

  setEnvInfo(getConfig().diligentApiHost);
}

//human-to-robot: converts waypoint friendly display name to waypoint mnemonic code
//
//NB: Returning moxSpotDisplayName as a fallback serves debugging, and has
// functional value if moxSpotDisplayName was unconverted code to start with
export function h2r(moxSpotDisplayName) {
  if (!codeMaps) {
    return moxSpotDisplayName;
  }

  let result = codeMaps.idsMoxspots.friendlyToCode[moxSpotDisplayName];
  result = result || codeMaps.waypointsMoxspots.friendlyToCode[moxSpotDisplayName];
  return result || moxSpotDisplayName;
}

//Station robot-to-human: converts waypoint mnemonic code to waypoint friendly display name.
export function r2h(waypoint_code) {
  if (!codeMaps) {
    return waypoint_code;
  }

  let result = codeMaps.idsMoxspots.codeToFriendly[waypoint_code];
  result = result || codeMaps.waypointsMoxspots.codeToFriendly[waypoint_code];
  return result || waypoint_code;
}


export function getR2HTable() {
  return codeMaps.idsMoxspots.codeToFriendly;
}

export function robotCodeToFriendly(robotCode) {
  if (!codeMaps) {
    return robotCode;
  }

  let result = codeMaps.robots.codeToFriendly[robotCode];
  return result || robotCode;
}

export function robotFriendlyToCode(robotFriendly) {
  if (!codeMaps) {
    return robotFriendly;
  }

  let result = codeMaps.robots.friendlyToCode[robotFriendly];
  return result || robotFriendly;
}


export async function initKioskSettings(socket, kioskSettingsHandler, addKioskHandler, deleteKioskHandler) {
  const kioskName = getUnitWp(true);

  if (kioskName) {
    socket.on(`kiosk_settings.${getConfig().site.mnem}`, (updatedKiosk) => {
      kioskSettingsHandler(updatedKiosk);
    });

    socket.on(`kiosk_settings.add.${getConfig().site.mnem}`, (updatedKiosk) => {
      addKioskHandler(updatedKiosk);
    });

    socket.on(`kiosk_settings.delete.${getConfig().site.mnem}`, (updatedKiosk) => {
      deleteKioskHandler(updatedKiosk);
    });

    let kiosks;
    const response = await getKioskSettings(getConfig().site.mnem);
    if (response && response.kiosks) {
      kiosks = response.kiosks;
      kioskSettingsHandler(kiosks);
    }
  }
}


/**
 * Sort a task's steps based on execution order.
 * TODO: Find a better way to put the steps in order (or have the api return them in correct order)
 * @param {Array} steps - A task's steps.
 * @param {string} type - The task type.
 * @param {Object[]} orderedSteps - Ordered steps data.
 * @returns {Object[]}
 */
export function sortSteps(steps, type, orderedSteps) {
  let sortedSteps = [];

  try {
    const getStepId = (steps, id) => {
      return steps.find((s) => s.id === id).id || '';
    };

    const completedSteps = steps.filter((s) => s.status !== STEP_STATUS.TO_DO && s.status !== STEP_STATUS.IN_PROGRESS);

    if (type === TASK_TYPE.DELIVERY) {
      if (orderedSteps.length === 4) {
        // No steps have completed
        orderedSteps.forEach((os) => {
          sortedSteps.push(getStepId(steps, os.step.id));
        });
      }
      else if (orderedSteps.length === 3) {
        // One step has completed
        if (completedSteps.length) {
          sortedSteps.push(completedSteps[0]?.id);
        }

        orderedSteps.forEach((os) => {
          sortedSteps.push(getStepId(steps, os.step.id));
        });
      }
      else if (orderedSteps.length === 2) {
        // Two steps have completed (GO_TO and LOAD)
        const gotoStep = completedSteps.find((cs) => cs.type === STEP_TYPE.GO_TO);
        if (gotoStep) {
          sortedSteps.push(gotoStep.id);
        }

        const loadStep = completedSteps.find((cs) => cs.type === STEP_TYPE.LOAD);
        if (loadStep) {
          sortedSteps.push(loadStep.id);
        }

        orderedSteps.forEach((os) => {
          sortedSteps.push(getStepId(steps, os.step.id));
        });
      }
      else if (orderedSteps.length === 1) {
        // Three steps have completed (GO_TO and LOAD and GO_TO)
        // Sort completed steps by timestamp (can't think of a better way to sort completed steps)
        const sortedCompletedSteps = completedSteps.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        sortedSteps = sortedCompletedSteps.map((s) => s.id);
        sortedSteps.push(getStepId(steps, orderedSteps[0].step.id));
      }
    }
    else if (type === TASK_TYPE.NAVIGATION) {
      if (orderedSteps.length === 1) {
        sortedSteps.push(getStepId(steps, orderedSteps[0].step.id));
      }
    }
    else if (type === TASK_TYPE.CHARGE) {
      if (orderedSteps.length === 2) {
        // No steps have completed
        orderedSteps.forEach((os) => {
          sortedSteps.push(getStepId(steps, os.step.id));
        });
      }
      else if (orderedSteps.length === 1) {
        // One step has completed
        if (completedSteps.length) {
          sortedSteps.push(completedSteps[0]?.id);
        }

        orderedSteps.forEach((os) => {
          sortedSteps.push(getStepId(steps, os.step.id));
        });
      }
    }
  }
  catch (e) {
    sortedSteps = [];
    console.error('Error sorting steps: ', e);
  }

  return sortedSteps;
}

/**
 * Get batch task details.
 * @param {Object} task - A task.
 * @param {Object[]} tasks - All tasks.
 * @returns {Object}
 */
function getBatchDetails(task, tasks) {
  try {
    const batchType = task.tags.includes(TASK_TAG.BATCH_1M) ? 'oneToMany' : 'manyToOne';
    const batchTasks = tasks.filter((t) => t.batchId === task.batchId);
    const sortedTasks = sortTasks(batchTasks);
    const startLocations = sortedTasks.map((t) => t.startLocations[0]);
    const endLocations = sortedTasks.map((t) => t.endLocations[0]);
    let stops = [];

    stops = startLocations.slice();
    stops.push(endLocations[endLocations.length - 1]);

    return {
      id: task.batchId,
      total: batchTasks.length,
      index: sortedTasks.findIndex((t) => t.id === task.id),
      batchType,
      stops,
    };
  }
  catch (e) {
    console.error('Error getting batch details: ', e);
    return {};
  }
}

/**
 * Sort tasks based on actualOrder (ordered steps)
 * @param {Object[]} tasks - All tasks.
 * @returns {Object[]}
 */
export function sortTasks(tasks) {
  try {
    const sortedTasks = tasks.sort((a, b) => {
      if (a.orderedSteps.length && b.orderedSteps.length) {
        return a.orderedSteps[0].actualOrder - b.orderedSteps[0].actualOrder;
      }
      else if (a.orderedSteps.length && b.resolvedAt) {
        return 1;
      }
      else if (a.resolvedAt && b.orderedSteps.length) {
        return -1;
      }
      else {
        return new Date(a.resolvedAt) - new Date(b.resolvedAt);
      }
    });
    return sortedTasks;
  }
  catch (e) {
    console.error('Error sorting tasks: ', e);
    return tasks;
  }
}

/**
 * Get ordered steps for a task, sorted by actualOrder
 * @param {Object} task - task data.
 * @param {Object[]} orderedSteps - Ordered steps data.
 * @returns {Object[]}
 */
export function getTaskOrderedSteps(taskId, orderedSteps) {
  try {
    if (!taskId || !orderedSteps) return [];
    return orderedSteps.filter((os) => os.taskId === taskId).sort((a, b) => a.actualOrder - b.actualOrder);
  }
  catch (e) {
    console.log('Error getting task ordered steps: ', e);
    return [];
  }
}

/**
 * Map task data from Scheduler V2 to existing data structure.
 * @param {Object} task - A task.
 * @param {Object[]} orderedSteps - Ordered steps data.
 * @param {Object[]} tasksData - All tasks data.
 * @returns {Object}
 */
export function mapTaskForV2(task, orderedSteps) {
  try {
    if (!(orderedSteps instanceof Array)) return null;
    const taskOrderedSteps = getTaskOrderedSteps(task.id, orderedSteps);

    const currentStep = () => {
      const orderedStep = taskOrderedSteps.find((os) => os.step.status === STEP_STATUS.IN_PROGRESS);
      return orderedStep ? orderedStep.step.id : '';
    };

    const data = {
      uuid: task.id,
      timestamp: task.createdAt,
      originalTimestamp: task.originalTimestamp,
      task_type: task.type,
      status: task.status,
      tags: task.tags,
      task_level_args: {
        end_goal: { value: task.endLocations[0] || [] },
      },
      task_data: {
        sender_note: task.publicNote || '',
        test: task.tags.includes(TASK_TAG.TEST),
        restricted: task.requiredRole === REQUIRED_ROLE.RESTRICTED,
      },
      steps: sortSteps(task.steps, task.type, taskOrderedSteps),
      orderedSteps: taskOrderedSteps,
      requested_by: task.createdForId,
      current_step: currentStep(),
      created: task.createdAt,
    };

    if (task.privateNote) {
      data.task_data.post_task_data = { note: task.privateNote };
    }

    if (task.batchId) {
      const getBatchType = () => {
        if (task.tags.includes(TASK_TAG.BATCH_1M)) return 'oneToMany';
        if (task.tags.includes(TASK_TAG.BATCH_M1)) return 'manyToOne';
        return null;
      };

      data.task_data = {
        ...data.task_data,
        batch: {
          id: task.batchId,
          batchType: getBatchType(),
          index: task.index,
          total: task.total,
          stops: task.stops,
          uuids: task.uuids,
        },
      };
    }

    if (task.startLocations?.length) {
      data.task_level_args.start_goal = { value: task.startLocations[0] || [] };
    }
    return data;
  }
  catch (e) {
    console.error('Error mapping task data: ', e);
    return null;
  }
}

/**
 * Map all tasks from Scheduler V2 to existing data structure.
 * @param {Object[]} tasks - All tasks.
 * @param {Object[]} orderedSteps - Ordered steps data.
 * @returns {Object[] || Array}
 */
export function mapTasksForV2(tasks, orderedSteps) {
  try {
    return tasks.map((task) => {
      return mapTaskForV2(task, orderedSteps);
    });
  }
  catch (e) {
    console.error('Error mapping tasks data: ', e);
    return [];
  }
}

/**
 * Get a robot's friendly name.
 * @param {string} uuid - A robot uuid.
 * @param {Object[]} robots - All robot data.
 * @returns {string || null}
 */
export function getRobotFriendlyId(uuid, robots) {
  try {
    const robot = robots.find((robot) => robot.id === uuid);
    if (robot) return robot.friendlyId;
    return null;
  }
  catch (e) {
    return null;
  }
}

/**
 * Map ordered step data from Scheduler V2 to existing data structure.
 * @param {Object[]} tasks - All tasks.
 * @param {Object[]} orderedSteps - Ordered steps data.
 * @param {string} robotName - A robot friendly name.
 * @returns {Object[] || Array}
 */
export function mapOrderedSteps(orderedSteps, tasks, robotName) {
  let mappedSteps = [];

  try {
    const getTaskType = (step) => {
      const selectedTask = tasks.find((t) => t.uuid === step.taskId);
      return selectedTask ? selectedTask.task_type : '';
    };

    const getStartGoal = (step) => {
      const selectedTask = tasks.find((t) => t.uuid === step.taskId);

      return selectedTask ? selectedTask.task_level_args.start_goal?.value : '';
    };

    const getEndGoal = (step) => {
      const selectedTask = tasks.find((t) => t.uuid === step.taskId);

      return selectedTask ? selectedTask.task_level_args.end_goal.value : '';
    };

    const getStepIndex = (step) => {
      const selectedTask = tasks.find((t) => t.uuid === step.taskId);

      return selectedTask ? selectedTask.steps.findIndex((s) => s === step.id) : 0;
    };

    const robot = _robotData.find((rd) => rd.friendlyId === robotName);

    const robotOrderedSteps = robot ? orderedSteps.filter((os) => os.robotId === robot.id) : orderedSteps;

    mappedSteps = robotOrderedSteps.map((os) => ({
      task_uuid: os.step.taskId,
      task_type: getTaskType(os.step),
      step_id: os.step.id,
      step_type: os.step.type,
      step_index: getStepIndex(os.step),
      task_args: {
        start_goal: {
          value: getStartGoal(os.step),
        },
        end_goal: {
          value: getEndGoal(os.step),
        },
      },
      robotId: getRobotFriendlyId(os.robotId, _robotData),
    }));
  }
  catch (e) {
    console.error('Error mapping ordered steps: ', e);
    mappedSteps = [];
  }
  return mappedSteps;
}

/**
 * Convert createdAt to seconds.
 * @param {Object} task - A task.
 * @returns {number || null}
 */
export function getSeconds(task) {
  try {
    return new Date(task.created).getTime() / 1000 || null;
  }
  catch (e) {
    console.error('Error getting seconds from task: ', e);
    return null;
  }
}

/**
 * Get the oldest createdAt timestamp (in seconds) for all tasks.
 * @param {Object[]} tasks - All tasks.
 * @returns {number || null}
 */
export function getEarliestSeconds(tasks) {
  const sortedTasks = tasks.slice().sort((a, b) => new Date(b.created) - new Date(a.created));
  return getSeconds(sortedTasks[0]);
}

/**
 * Filter tasks and limit to the past 2 days.
 * This is replication of the snapshot handler (initTaskSubscription) method
 * in TrackedTask. Re-using it here for Scheduler V2 to ensure compatiability.
 * @param {Object[]} tasksData - Task data.
 * @param {Object[]} orderedSteps - Ordered steps data.
 */
export function getTimeDescTasks(tasksData, orderedSteps) {
  const nowTicks = Date.now();
  const tasks = mapTasksForV2(tasksData, orderedSteps);
  tasks.sort((a, b) => new Date(b.created) - new Date(a.created));

  //filter down to the past two days.  Six hours is probably
  //enough in a practical sense, but keeping a layer of conservativeness.
  //Also filter in only allowed template types.
  const cutoffSeconds = (nowTicks - 2 * ONE_DAY_TICKS) / 1000;
  const timeDescOrderData = [];
  for (let task of tasks) {
    if (getSeconds(task) <= cutoffSeconds) break;
    if ([TASK_TYPE.DELIVERY].indexOf(task.task_type) >= 0) timeDescOrderData.push(task);
  }

  return timeDescOrderData;
}

/**
 * Adds the current_step property to all tasks.
 * To ensure compatability with current data structure.
 * @param {Object[]} tasks - Task data.
 * @param {Object[]} steps - Ordered steps data.
 * @returns {Object[]}
 */
function updateCurrentStep(steps, tasks) {
  const getCurrentStep = (task) => {
    const currStep = steps.find((s) => s.task_uuid === task.uuid);
    return currStep ? currStep.step_id : undefined;
  };

  return tasks.map((task) => ({
    ...task,
    current_step: getCurrentStep(task),
  }));
}

/**
 * Re-map robot state from task-service
 * to ensure compatability with current data structure.
 * @param {Object[]} data - Robots data.
 * @returns {Object[] || Array}
 */
function mapRobotData(data) {
  /*
    It's time to move on from this taskService-era routine and the
    routines that depend on it; maintaining it is becoming confusing and 
    of dubious value. 
  */
  throw new Error('mapRobotData is now deprecated');

  const robots = [];
  try {
    data.forEach((robot) => {
      robots.push({
        // id: robot.friendlyId,
        // acceptingTasks: robot.availableForTasking,
        // currentTask: getCurrentTaskId(robot.id),
        // semanticRoom: robot.room,
        // taskStatus: robot.status,
        // uuid: robot.id,
      });
    });
    return robots;
  }
  catch (e) {
    console.error('Error mapping robot data: ', e);
    return robots;
  }
}

/**
 * Updates all robot data after robot.update socket emits a change for a robot.
 * @param {Object[]} data - Robot data.
 * @returns {Object[] || Array}
 */
async function updateRobots(data) {
  const existingRobotIdx = _robotData.findIndex((r) => r.id === data.id);

  if (existingRobotIdx >= 0) {
    _robotData[existingRobotIdx] = data;
  }
  else {
    _robotData.push(data);
  }

  return mapRobotData(_robotData);
}

/**
 * Gets mapped robot data
 * @returns {Object[] || Array}
 */
export function getMappedRobots() {
  return mapRobotData(_robotData);
}

/**
 * Checks whether a task is complete.
 * @param {string} task - A task uuid.
 * @returns {boolean}
 */
function isTaskComplete(task) {
  const taskDetails = _taskData.find((t) => t.id === task);

  if (taskDetails) {
    return !(
      taskDetails.status !== TASK_STATUS.COMPLETED &&
      taskDetails.status !== TASK_STATUS.CANCELED &&
      taskDetails.status !== TASK_STATUS.IN_PROGRESS
    );
  }
  return false;
}

/**
 * Gets the sorted tasks for the task queue.
 * @param {Object} queue - The task queue object.
 * @returns {Object[] || Array}
 */
function getOrderedTasks(queue) {
  let queueTasks = [];
  let robotId = null;

  if (queue !== 'triage') {
    const robot = _robotData.find((r) => r.friendlyId === queue);

    if (robot) {
      robotId = robot.id || null;
    }
  }

  // Get ordered steps for queue
  const robotSteps = _orderedSteps.filter((os) => os.robotId === robotId).sort((a, b) => a.actualOrder - b.actualOrder);

  // Put tasks into queue
  robotSteps.forEach((os) => {
    if (!queueTasks.includes(os.taskId) && !isTaskComplete(os.taskId)) {
      queueTasks.push(os.taskId);
    }
  });

  return queueTasks;
}

/**
 * Initializes the (empty) task queue.
 * @returns {Object[] || Array}
 */
function initTaskQueues() {
  let queues = { triage: [] };

  const robots = _robotData.map((r) => r.friendlyId);

  if (robots && robots.length) {
    robots.forEach((robot) => {
      queues[robot] = [];
    });
  }

  return queues;
}

/**
 * Gets the task id of the in progress task
 */
function getCurrentTaskId(robotId) {
  if (!robotId) return null;

  let task = _taskData.find(
    (t) =>
      t.robotId === robotId &&
      (t.status === TASK_STATUS.FAILED ||
        t.status === TASK_STATUS.INTERRUPTED ||
        t.status === TASK_STATUS.IN_PROGRESS ||
        t.status === TASK_STATUS.CANCEL_REQUESTED ||
        t.status === TASK_STATUS.CLAIMED),
  );

  if (!task) return null;

  return task.id;
}

/**
 * Updates the task queue data.
 * @param {requestCallback} taskQueuesHandler - The callback that handles task queues data.
 */
function updateTaskQueues(taskQueuesHandler) {
  let queues = {};
  const robotsTasks = {};
  const updatedQueues = {};
  let version_stamp = 0;

  if (Object.keys(_taskQueues).length) {
    queues = _taskQueues.queues;
    version_stamp = _taskQueues.version_stamp + 1;
  }
  else {
    queues = initTaskQueues();
  }

  Object.keys(queues).forEach((queue) => {
    updatedQueues[queue] = getOrderedTasks(queue);
  });

  if (_robotData.length) {
    _robotData.forEach((r) => {
      const currentTaskId = getCurrentTaskId(r.id);
      if (currentTaskId) {
        robotsTasks[r.friendlyId] = currentTaskId;
      }
    });
  }

  const updatedTaskQueues = {
    version_stamp,
    queues: updatedQueues,
    robots_tasks: robotsTasks,
  };

  _taskQueues = updatedTaskQueues;

  taskQueuesHandler(updatedTaskQueues);
}

export function refreshedSnapshot(logLabel, preSnapProducerFunc, optStructParam, snapHandler) {
  let lastErrTicks = 0,
    lastErrRefreshTicks = 0;

  let errHandler = (error) => {
    lastErrTicks = Date.now();
    console.log(`** refreshedSnapshot: Error in ${logLabel}: ` + error);
    console.log(error);
    elasticLogError({ what: 'fsErrHandler', logLabel, msg: `${logLabel} err handler: ` + error });
  };

  let paramsPlusErrHandler = [snapHandler, errHandler];
  if (optStructParam) paramsPlusErrHandler.unshift(optStructParam);

  let curSubscription,
    looping = true;
  curSubscription = preSnapProducerFunc().onSnapshot(...paramsPlusErrHandler);
  let lastRefreshTicks = Date.now();
  let refreshLoop = async () => {
    while (looping) {
      try {
        await sleep(1000 * 30);

        let firestoreRefreshMinutes = getConfig().firestoreRefreshMinutes;
        if (!firestoreRefreshMinutes) continue;

        const nowTicks = Date.now();
        const needErrRefresh = lastErrTicks && nowTicks - lastErrRefreshTicks > 1000 * 60 * 3;
        let strideMinutes = firestoreRefreshMinutes * (0.95 + 0.1 * Math.random()); //perturb to amortize wrt other subscription refreshes
        if (nowTicks - lastRefreshTicks > 1000 * 60 * strideMinutes || needErrRefresh) {
          let msg = `Refreshed firestore ${logLabel} ${
            needErrRefresh ? 'because of error' : `per ${firestoreRefreshMinutes}m interval`
          }`;
          elasticLogEvent({ what: 'fsRefresh', logLabel, firestoreRefreshMinutes, perErr: !!needErrRefresh, msg });

          curSubscription(); //close the current subscription
          curSubscription = undefined;

          lastErrTicks = 0; //clear the error time

          curSubscription = preSnapProducerFunc().onSnapshot(...paramsPlusErrHandler);
          lastRefreshTicks = nowTicks;
          lastErrRefreshTicks = needErrRefresh ? nowTicks : 0;
        }
      }
      catch (err) {
        elasticLogError({ what: 'fsRefreshLoopErr', logLabel, msg: `${logLabel} refresh err: ` + err });
      }
    }
  };
  refreshLoop(); //async

  return () => {
    looping = false;
    if (curSubscription) {
      curSubscription();
      curSubscription = undefined;
    }
  };
}
